// ============================================================================
// Merge Tickets — fold a customer's duplicate tickets into this one
// ============================================================================
// Opened from the ticket detail page. The ticket on screen is the PRIMARY (the
// one that stays); the agent ticks the same customer's other tickets that are
// about the same thing. support_merge_tickets() does the rest in one
// transaction: their messages move into this thread, they are closed and
// hidden from the customer, and the customer sees one line here saying what
// was merged. See migration 20261007000001_support_ticket_merge.sql.
//
// To keep a different ticket instead, open that ticket and merge from there.
// ============================================================================

import { useEffect, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Checkbox } from '@/components/ui/checkbox';
import { Textarea } from '@/components/ui/textarea';
import { Skeleton } from '@/components/ui/skeleton';
import { AlertTriangle, ArrowDown, Hash, Loader2, Lock, Merge, Pin } from 'lucide-react';
import { toast } from 'sonner';
import {
  listMergeCandidates, mergeBlockReason, mergeTickets, ticketTitle, ticketQueue,
  STATUS_LABELS, STATUS_CLASSES, SOURCE_CLASSES,
  type TicketRow, type MergeResult, type TicketQueue,
} from '@/lib/support';

// Where the customer opened each ticket. The same creator can have tickets in
// both the app and the BMC Coach portal, and those can be merged together
// (migration 20261027000001), so the dialog names the portal on every row.
const PORTAL_LABELS: Record<TicketQueue, string> = {
  creator: 'Creator app',
  coach: 'BMC Coach portal',
  brand: 'Brand dashboard',
};

function PortalPill({ ticket }: { ticket: Pick<TicketRow, 'source' | 'is_coach' | 'cross_portal'> }) {
  const label = ticket.cross_portal ? 'App + Coach portal' : PORTAL_LABELS[ticketQueue(ticket)];
  const cls = ticket.cross_portal ? 'bg-indigo-100 text-indigo-800 border-indigo-200' : SOURCE_CLASSES[ticketQueue(ticket)];
  return (
    <span className={`inline-flex shrink-0 items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${cls}`}>
      {label}
    </span>
  );
}

export function MergeTicketsDialog({ open, onOpenChange, primary, onMerged }: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  primary: TicketRow;
  onMerged: (result: MergeResult) => void;
}) {
  const [selected, setSelected] = useState<Set<string>>(new Set());
  const [note, setNote] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (open) return;
    setSelected(new Set());
    setNote('');
    setError(null);
  }, [open]);

  const { data: candidates, isLoading } = useQuery({
    queryKey: ['support-merge-candidates', primary.id],
    queryFn: () => listMergeCandidates(primary),
    enabled: open,
  });

  const classified = (candidates ?? []).map((c) => ({ c, reason: mergeBlockReason(primary, c) }));
  const mergeable = classified.filter((x) => !x.reason).map((x) => x.c);
  const unavailable = classified.filter((x) => x.reason) as { c: TicketRow; reason: string }[];
  const primaryClosed = primary.status === 'closed';

  const toggle = (id: string) => {
    setSelected((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });
  };

  const submit = async () => {
    const duplicates = mergeable.filter((c) => selected.has(c.id));
    if (duplicates.length === 0) return;
    setSubmitting(true);
    setError(null);
    try {
      const result = await mergeTickets(primary, duplicates, note);
      toast.success(`Merged ${duplicates.length} ticket${duplicates.length === 1 ? '' : 's'} into #${primary.ticket_number ?? '—'}`);
      onMerged(result);
      onOpenChange(false);
    } catch (e: any) {
      setError(e?.message || 'Could not merge these tickets');
    } finally {
      setSubmitting(false);
    }
  };

  const count = selected.size;
  // Ticked tickets the customer opened in the other portal (app vs Coach).
  const otherPortalCount = mergeable.filter((c) => selected.has(c.id) && ticketQueue(c) !== ticketQueue(primary)).length;

  return (
    <Dialog open={open} onOpenChange={(o) => { if (!submitting) onOpenChange(o); }}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Merge className="h-4 w-4" /> Merge into #{primary.ticket_number ?? '—'}
          </DialogTitle>
          <DialogDescription>
            The ticket below stays. The ones you select are closed, hidden from the customer, and their
            messages move into it. This can't be undone.
          </DialogDescription>
        </DialogHeader>

        {/* The ticket that stays — everything picked below is folded into it. */}
        <div className="rounded-lg border-2 border-primary/50 bg-primary/5 p-2.5">
          <div className="mb-1 flex items-center gap-1.5 text-[10px] font-bold uppercase tracking-wide text-primary">
            <Pin className="h-3 w-3" /> Main ticket — this one stays
          </div>
          <div className="flex items-center gap-2">
            <span className="inline-flex items-center gap-0.5 font-mono text-xs font-semibold tabular-nums">
              <Hash className="h-3 w-3" />{primary.ticket_number ?? '—'}
            </span>
            <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${STATUS_CLASSES[primary.status]}`}>
              {STATUS_LABELS[primary.status]}
            </span>
            <PortalPill ticket={primary} />
            {primary.merged_ticket_count > 0 && (
              <span className="ml-auto text-[10px] text-muted-foreground">{primary.merged_ticket_count} already merged in</span>
            )}
          </div>
          <p className="mt-0.5 truncate text-sm font-medium">{ticketTitle(primary)}</p>
        </div>

        {primaryClosed && (
          <p className="text-xs text-destructive">
            This ticket is closed. Reopen it first, or open the ticket you want to keep and merge from there.
          </p>
        )}

        <div className="flex items-center gap-2 text-xs font-medium text-muted-foreground">
          <ArrowDown className="h-3.5 w-3.5" />
          Select tickets to fold into #{primary.ticket_number ?? '—'}
        </div>

        <div className="max-h-[260px] space-y-1.5 overflow-y-auto pr-1">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => <Skeleton key={i} className="h-14 w-full rounded-lg" />)
          ) : mergeable.length === 0 && unavailable.length === 0 ? (
            <p className="py-6 text-center text-sm text-muted-foreground">
              This customer has no other tickets to merge.
            </p>
          ) : (
            <>
              {mergeable.length === 0 && (
                <p className="py-3 text-center text-sm text-muted-foreground">
                  No other tickets available to merge.
                </p>
              )}
              {mergeable.map((c) => {
                const checked = selected.has(c.id);
                return (
                  <label
                    key={c.id}
                    className={`flex cursor-pointer items-start gap-3 rounded-lg border p-2.5 transition-colors ${
                      checked ? 'border-primary/50 bg-primary/5' : 'border-border hover:bg-muted/50'
                    }`}
                  >
                    <Checkbox checked={checked} onCheckedChange={() => toggle(c.id)} className="mt-0.5" disabled={submitting} />
                    <div className="min-w-0 flex-1">
                      <div className="flex items-center gap-2">
                        <span className="inline-flex items-center gap-0.5 font-mono text-xs font-semibold tabular-nums text-muted-foreground">
                          <Hash className="h-3 w-3" />{c.ticket_number ?? '—'}
                        </span>
                        <span className={`inline-flex items-center rounded-full border px-1.5 py-0 text-[10px] font-semibold ${STATUS_CLASSES[c.status]}`}>
                          {STATUS_LABELS[c.status]}
                        </span>
                        <PortalPill ticket={c} />
                        <span className="ml-auto shrink-0 text-[10px] text-muted-foreground">
                          {c.last_activity_at ? formatDistanceToNow(new Date(c.last_activity_at), { addSuffix: true }) : ''}
                        </span>
                      </div>
                      <p className="mt-0.5 truncate text-sm font-medium">{ticketTitle(c)}</p>
                      {c.last_message_preview && (
                        <p className="truncate text-xs text-muted-foreground">{c.last_message_preview}</p>
                      )}
                    </div>
                  </label>
                );
              })}
              {unavailable.length > 0 && (
                <>
                  <p className="pt-1 text-[10px] font-semibold uppercase tracking-wide text-muted-foreground">
                    Can't be merged into this ticket
                  </p>
                  {unavailable.map(({ c, reason }) => (
                    <div key={c.id} className="flex items-start gap-3 rounded-lg border border-dashed p-2.5 opacity-60">
                      <Lock className="mt-0.5 h-4 w-4 shrink-0 text-muted-foreground" />
                      <div className="min-w-0 flex-1">
                        <div className="flex items-center gap-2">
                          <span className="inline-flex items-center gap-0.5 font-mono text-xs font-semibold tabular-nums text-muted-foreground">
                            <Hash className="h-3 w-3" />{c.ticket_number ?? '—'}
                          </span>
                          <PortalPill ticket={c} />
                          <span className="text-[10px] font-medium text-muted-foreground">
                            {reason}
                          </span>
                        </div>
                        <p className="mt-0.5 truncate text-sm">{ticketTitle(c)}</p>
                      </div>
                    </div>
                  ))}
                </>
              )}
            </>
          )}
        </div>

        <div className="space-y-1.5">
          <Textarea
            value={note}
            onChange={(e) => setNote(e.target.value)}
            placeholder="Reason (optional, internal — shown in the activity log)"
            maxLength={1000}
            rows={2}
            disabled={submitting}
          />
        </div>

        {otherPortalCount > 0 && (
          <div className="flex items-start gap-2 rounded-md border border-amber-200 bg-amber-50 p-2.5 text-xs text-amber-900">
            <AlertTriangle className="mt-0.5 h-3.5 w-3.5 shrink-0" />
            <span>
              {otherPortalCount === 1 ? '1 selected ticket was' : `${otherPortalCount} selected tickets were`} opened
              in the {PORTAL_LABELS[ticketQueue(primary) === 'coach' ? 'creator' : 'coach']}, not
              the {PORTAL_LABELS[ticketQueue(primary)]}. Same customer account, different portal. After merging,
              #{primary.ticket_number ?? '—'} shows in both the creator app and the BMC Coach portal.
            </span>
          </div>
        )}

        {error && <p className="text-sm text-destructive">{error}</p>}

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)} disabled={submitting}>Cancel</Button>
          <Button onClick={submit} disabled={count === 0 || submitting || primaryClosed}>
            {submitting ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Merge className="mr-2 h-4 w-4" />}
            {count === 0 ? 'Merge' : `Merge ${count} ticket${count === 1 ? '' : 's'}`}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
